<template>
  <AppLayout 
    page-title="Payment Center" 
    page-subtitle="Track your payments, view invoices, and manage your earnings"
  >
    <!-- Page Header -->
    <div class="mb-8 pt-8">
      <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
        <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
        Payments & Invoicing
      </div>
      <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">Payment Center</h1>
      <p class="text-xl text-slate-300 mb-8 max-w-2xl">Track your payments, view invoices, and manage your earnings</p>
    </div>

    <!-- Quick Stats -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <div class="payment-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Total Earned</p>
          <p class="text-3xl font-bold text-white">${{ paymentStats.totalEarned }}</p>
          <p class="text-sm text-emerald-400">This year</p>
        </div>
      </div>

      <div class="payment-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Pending</p>
          <p class="text-3xl font-bold text-white">${{ paymentStats.pendingAmount }}</p>
          <p class="text-sm text-amber-400">{{ paymentStats.pendingCount }} payments</p>
        </div>
      </div>

      <div class="payment-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-slate-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-slate-300" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092c.938-.177 1.791-.604 2.353-1.253a1 1 0 10-1.51-1.31c.163.187.452.377.843.504v-1.941c-.622-.117-1.196-.342-1.676-.662C8.602 9.765 8 8.99 8 8c0-.99.602-1.765 1.324-2.246.48.32 1.054.545 1.676.662V7a1 1 0 102 0v.092z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">This Month</p>
          <p class="text-3xl font-bold text-white">${{ paymentStats.monthlyEarnings }}</p>
          <p class="text-sm text-blue-400">{{ paymentStats.percentageChange >= 0 ? '+' : '' }}{{ paymentStats.percentageChange }}% from last month</p>
        </div>
      </div>

      <div class="payment-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Completed Jobs</p>
          <p class="text-3xl font-bold text-white">{{ paymentStats.completedJobs }}</p>
          <p class="text-sm text-purple-400">This year</p>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <button @click="exportPaymentData" class="quick-action-card">
        <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
        </div>
        <div>
          <p class="text-white font-medium">Export Data</p>
          <p class="text-sm text-slate-400">Download payment history</p>
        </div>
      </button>

      <button @click="createInvoice" class="quick-action-card">
        <div class="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
        </div>
        <div>
          <p class="text-white font-medium">Create Invoice</p>
          <p class="text-sm text-slate-400">Generate new invoice</p>
        </div>
      </button>

      <button @click="loadMorePayments" class="quick-action-card">
        <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </div>
        <div>
          <p class="text-white font-medium">Load More</p>
          <p class="text-sm text-slate-400">View more payments</p>
        </div>
      </button>
    </div>

    <!-- Payment History -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-white">Payment History</h2>
        <div class="flex space-x-2">
          <button 
            @click="activeFilter = 'all'"
            :class="activeFilter === 'all' ? 'bg-emerald-500 text-white' : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50'"
            class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            All
          </button>
          <button 
            @click="activeFilter = 'pending'"
            :class="activeFilter === 'pending' ? 'bg-emerald-500 text-white' : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50'"
            class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            Pending
          </button>
          <button 
            @click="activeFilter = 'completed'"
            :class="activeFilter === 'completed' ? 'bg-emerald-500 text-white' : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50'"
            class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            Completed
          </button>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b border-slate-700/50">
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Job</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Client</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Amount</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Status</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Date</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="payment in filteredPayments" :key="payment.id" class="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors">
              <td class="py-4 px-4">
                <div>
                  <p class="text-white font-medium">{{ payment.jobTitle }}</p>
                  <p class="text-sm text-slate-400">{{ payment.jobId }}</p>
                </div>
              </td>
              <td class="py-4 px-4">
                <div>
                  <p class="text-white font-medium">{{ payment.clientName }}</p>
                  <p class="text-sm text-slate-400">{{ payment.clientCompany }}</p>
                </div>
              </td>
              <td class="py-4 px-4">
                <p class="text-white font-bold">${{ payment.amount }}</p>
              </td>
              <td class="py-4 px-4">
                <span :class="getStatusClass(payment.status)" class="px-3 py-1 rounded-full text-xs font-medium">
                  {{ payment.status }}
                </span>
              </td>
              <td class="py-4 px-4">
                <p class="text-white">{{ formatDate(payment.date) }}</p>
                <p class="text-sm text-slate-400">{{ formatTime(payment.date) }}</p>
              </td>
              <td class="py-4 px-4">
                <div class="flex space-x-2">
                  <button type="button" class="text-emerald-400 hover:text-emerald-300 transition-colors" @click="router.push('/invoices')">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                  </button>
                  <button type="button" class="text-blue-400 hover:text-blue-300 transition-colors" @click="router.push('/invoices')">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Recent Invoices -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-xl font-bold text-white mb-4">Recent Invoices</h3>
        <div class="space-y-4">
          <button
            v-for="invoice in recentInvoices"
            :key="invoice.id"
            type="button"
            class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg w-full text-left hover:bg-slate-700/50"
            @click="router.push(`/invoices?id=${invoice.id}`)"
          >
            <div>
              <p class="text-white font-medium">{{ invoice.jobTitle }}</p>
              <p class="text-sm text-slate-400">{{ invoice.invoiceNumber }}</p>
            </div>
            <div class="text-right">
              <p class="text-white font-bold">${{ invoice.amount }}</p>
              <p class="text-sm text-slate-400">{{ formatDate(invoice.issuedDate) }}</p>
            </div>
          </button>
          <router-link v-if="recentInvoices.length" to="/invoices" class="block text-sm text-emerald-400 hover:text-emerald-300">
            View all invoices →
          </router-link>
        </div>
      </div>

      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-xl font-bold text-white mb-4">Payment Methods</h3>
        <div class="space-y-4">
          <div class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">Direct Deposit</p>
                <p class="text-sm text-slate-400">Primary method</p>
              </div>
            </div>
            <button class="text-emerald-400 hover:text-emerald-300 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
            </button>
          </div>

          <div class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">PayPal</p>
                <p class="text-sm text-slate-400">johndoe@email.com</p>
              </div>
            </div>
            <button class="text-emerald-400 hover:text-emerald-300 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppLayout from '../components/layout/AppLayout.vue'
import { useUserStore } from '../stores/userStore'

const router = useRouter()

interface Payment {
  id: number
  jobTitle: string
  jobId: string
  clientName: string
  clientCompany: string
  amount: string
  status: string
  date: string
  paymentMethod?: string
  transactionId?: string
  notes?: string
}

interface Invoice {
  id: number
  invoiceNumber: string
  jobTitle: string
  jobId: string
  clientName: string
  clientCompany: string
  amount: string
  status: string
  dueDate: string
  issuedDate: string
  paidDate?: string
  notes?: string
}

const userStore = useUserStore()

// Reactive data
const isLoading = ref(true)
const activeFilter = ref('all')
const currentPage = ref(1)
const totalPages = ref(1)

const paymentStats = ref({
  totalEarned: '0',
  pendingAmount: '0',
  pendingCount: 0,
  monthlyEarnings: '0',
  percentageChange: 0,
  completedJobs: 0
})

const payments = ref<Payment[]>([])
const recentInvoices = ref<Invoice[]>([])

// Computed properties
const filteredPayments = computed(() => {
  if (activeFilter.value === 'all') {
    return payments.value
  }
  return payments.value.filter(payment => payment.status.toLowerCase() === activeFilter.value)
})

// API Methods
const fetchPaymentStats = async () => {
  try {
    const response = await fetch('/api/payments/stats', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })

    if (response.ok) {
      const data = await response.json()
      paymentStats.value = data
    } else {
      console.error('Error fetching payment stats')
    }
  } catch (error) {
    console.error('Error fetching payment stats:', error)
  }
}

const fetchPayments = async () => {
  try {
    const params = new URLSearchParams({
      page: currentPage.value.toString(),
      limit: '20'
    })

    if (activeFilter.value !== 'all') {
      params.append('status', activeFilter.value)
    }

    const response = await fetch(`/api/payments/history?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })

    if (response.ok) {
      const data = await response.json()
      payments.value = data.payments
      totalPages.value = data.pagination.totalPages
    } else {
      console.error('Error fetching payments')
    }
  } catch (error) {
    console.error('Error fetching payments:', error)
  }
}

const fetchInvoices = async () => {
  try {
    const response = await fetch('/api/payments/invoices?limit=5', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })

    if (response.ok) {
      const data = await response.json()
      recentInvoices.value = data.invoices
    } else {
      console.error('Error fetching invoices')
    }
  } catch (error) {
    console.error('Error fetching invoices:', error)
  }
}

const loadMorePayments = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
    fetchPayments()
  }
}

const exportPaymentData = () => {
  const csvContent = [
    ['Job Title', 'Job ID', 'Client', 'Company', 'Amount', 'Status', 'Date', 'Payment Method'],
    ...payments.value.map(payment => [
      payment.jobTitle,
      payment.jobId,
      payment.clientName,
      payment.clientCompany,
      payment.amount,
      payment.status,
      payment.date,
      payment.paymentMethod || 'N/A'
    ])
  ].map(row => row.join(',')).join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `payments-${new Date().toISOString().split('T')[0]}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

const createInvoice = () => {
  router.push('/invoices?new=1')
}

// Load data on mount
onMounted(async () => {
  await Promise.all([
    fetchPaymentStats(),
    fetchPayments(),
    fetchInvoices()
  ])
  isLoading.value = false
})

// Methods
const getStatusClass = (status: string) => {
  const classes: { [key: string]: string } = {
    'Completed': 'bg-emerald-500/90',
    'Pending': 'bg-yellow-500/90',
    'Processing': 'bg-blue-500/90',
    'Failed': 'bg-red-500/90'
  }
  return classes[status] || 'bg-slate-500/90'
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatTime = (dateString: string) => {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  })
}
</script>

<style scoped>
.payment-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 hover:border-emerald-500/50 transition-all duration-300;
}

.quick-action-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 hover:border-emerald-500/50 transition-all duration-300 text-left w-full;
}
</style>
